import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { StrictMode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { useGLTF } from '@react-three/drei';

import { Home } from 'src/screens/Home/Home';
import { Trainer } from 'src/screens/Trainer/Trainer';
import { ProgramGenerated as TrainerProgramGenerated } from 'src/screens/Trainer/screens/ProgramGenerated/ProgramGenerated';
import { ProgramCompleted } from 'src/screens/Trainer/screens/ProgramCompleted/ProgramCompleted';
// Onboarding Trainer
import { OnboardingTrainer } from 'src/screens/OnboardingTrainer/OnboardingTrainer';
import { Hero as OnboardingTrainerHero } from 'src/screens/OnboardingTrainer/screens/Hero/Hero';
import { Login as OnboardingTrainerLogin } from 'src/screens/OnboardingTrainer/screens/Login/Login';
import { SignUp as OnboardingTrainerSignUp } from 'src/screens/OnboardingTrainer/screens/SignUp/SignUp';
import { ForgotPassword as OnboardingTrainerForgotPassword } from 'src/screens/OnboardingTrainer/screens/ForgotPassword/ForgotPassword';
import { ResetPassword as OnboardingTrainerResetPassword } from 'src/screens/OnboardingTrainer/screens/ResetPassword/ResetPassword';
import { AuthAction as OnboardingTrainerAuthAction } from 'src/screens/OnboardingTrainer/screens/AuthAction/AuthAction';
import { EmailVerified as OnboardingTrainerEmailVerified } from 'src/screens/OnboardingTrainer/screens/EmailVerified/EmailVerified';
import { VerifyEmail as OnboardingTrainerVerifyEmail } from 'src/screens/OnboardingTrainer/screens/VerifyEmail/VerifyEmail';
import { Dashboard as OnboardingTrainerDashboard } from 'src/screens/OnboardingTrainer/screens/Dashboard/Dashboard';
import { Overview as DashboardOverview } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Overview/Overview';
import { Clients as DashboardClients } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Clients/Clients';
import { ClientOverview as DashboardClientOverview } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ClientOverview/ClientOverview';
import { OverviewTab } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ClientOverview/components/OverviewTab';
import { ClientNotesTab } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ClientOverview/components/ClientNotesTab';
import { ProgramTab } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ClientOverview/components/ProgramTab';
import { WorkoutsTab } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ClientOverview/components/WorkoutsTab';
import { ClientProfileTab } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ClientOverview/components/ClientProfileTab';
import { WorkoutDetails as DashboardWorkoutDetails } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/WorkoutDetails/WorkoutDetails';
import { Programs as DashboardPrograms } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Programs/Programs';
import { Profile as DashboardProfile } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Profile/Profile';
import { ProgramGeneratorAI } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ProgramGeneratorAI/ProgramGeneratorAI';
import { ProgramGenerated } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ProgramGenerated/ProgramGenerated';
import { DashboardMessagesLayout } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Messages/DashboardMessagesLayout';
import { DashboardMessagesIndexRoute } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Messages/DashboardMessagesIndexRoute';
import { DashboardChatDetails } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Messages/DashboardChatDetails';
import { DashboardMediaLinksFilesScreen } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Messages/DashboardMediaLinksFilesScreen';
import { CheckinDetailsRoute } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Checkins/CheckinDetailsRoute';
import { Schedule as DashboardSchedule } from 'src/screens/OnboardingTrainer/screens/Schedule/Schedule';
import {
  Welcome as SubscriptionWelcome,
  SubscriptionPlans,
  Checkout as SubscriptionCheckout,
  PrivacyPolicyScreen as SubscriptionPrivacyPolicy,
  TermsOfServiceScreen as SubscriptionTermsOfService,
  MySubscription,
  ManagePaymentMethod,
  BillingHistory,
  CancelSubscription,
  ReactivatePro,
  ContactSupport as SubscriptionContactSupport,
} from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/Subscription';

import { About } from 'src/screens/About/About';
import { NotFound } from 'src/screens/NotFound/NotFound';
import { ServerError } from 'src/screens/ServerError/ServerError';
import { Unauthorized } from 'src/screens/Unauthorized/Unauthorized';
import { Loading } from 'src/screens/Loading/Loading';
import { Maintenance } from 'src/screens/Maintenance/Maintenance';
import { ComingSoon } from 'src/screens/ComingSoon/ComingSoon';
import { AccountStatus } from 'src/screens/AccountStatus/ComingSoon.tsx';
import { PrivacyPolicy } from 'src/screens/PrivacyPolicy/PrivacyPolicy';
import { TermsOfService } from 'src/screens/TermsOfService/TermsOfService';

import UtilityLayout from 'src/layouts/UtilityLayout/UtilityLayout.tsx';
import ScrollToTop from './utils/scrollToTop';
import AppManager from 'src/components/AppManager/AppManager.tsx';
import { GuestRoute } from 'src/components/GuestRoute/GuestRoute.tsx';

import { queryClient } from 'src/api/queryClient.ts';
import { IS_DEV } from 'src/config/api-config.ts';

import model from 'src/assets/other/iphone.glb';

const container = document.getElementById('app')!;
const root = createRoot(container);

if (IS_DEV) {
  console.log('Dev Mode is on');
}

useGLTF.preload(model);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <>
          <ScrollToTop />
          <UtilityLayout />
        </>
      }
    >
      <Route path="/" element={<Home />} />
      <Route path="/trainer">
        <Route index element={<Trainer />} />
        <Route path="generated/:type" element={<TrainerProgramGenerated />} />
        <Route path="generated/:completeType/completed" element={<ProgramCompleted />} />
      </Route>
      <Route path="/onboarding-trainer" element={<OnboardingTrainer />}>
        <Route index element={<OnboardingTrainerHero />} />

        {/* Guest-only Auth Routes */}
        <Route element={<GuestRoute />}>
          <Route path="login" element={<OnboardingTrainerLogin />} />
          <Route path="signup" element={<OnboardingTrainerSignUp />} />
          <Route path="forgot-password" element={<OnboardingTrainerForgotPassword />} />
          <Route path="auth-action" element={<OnboardingTrainerAuthAction />} />
          <Route path="reset-password" element={<OnboardingTrainerResetPassword />} />
          <Route path="signup/verify-email" element={<OnboardingTrainerVerifyEmail />} />
          <Route path="signup/email-verified" element={<OnboardingTrainerEmailVerified />} />
        </Route>
        <Route path="dashboard" element={<OnboardingTrainerDashboard />}>
          <Route index element={<DashboardOverview />} />
          <Route path="welcome" element={<SubscriptionWelcome />} />
          <Route path="clients" element={<DashboardClients />} />
          <Route path="clients/:uid" element={<DashboardClientOverview />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<OverviewTab />} />
            <Route path="overview/notes" element={<ClientNotesTab />} />
            <Route path="program" element={<ProgramTab />} />
            <Route path="workouts" element={<WorkoutsTab />} />
            <Route path="client-profile" element={<ClientProfileTab />} />
            <Route path="workouts/:workoutId" element={<DashboardWorkoutDetails />} />
          </Route>
          <Route path="programs" element={<DashboardPrograms />} />
          <Route path="messages" element={<DashboardMessagesLayout />}>
            <Route index element={<DashboardMessagesIndexRoute />} />
            <Route path=":chatId" element={<DashboardChatDetails />} />
            <Route path=":chatId/media" element={<DashboardMediaLinksFilesScreen />} />
            <Route path="checkin/:checkinId" element={<CheckinDetailsRoute />} />
          </Route>
          <Route path="profile" element={<DashboardProfile />} />
          <Route path="profile/schedule" element={<DashboardSchedule mode="profile" />} />
          <Route path="program-generator/:by" element={<ProgramGeneratorAI />} />
          <Route path="program-generated" element={<ProgramGenerated />} />
          <Route path="program-generated/:programId" element={<ProgramGenerated />} />
          <Route path="program-generated/:programId/:workout" element={<ProgramGenerated />} />

          {/* Subscription Flow Routes */}
          <Route path="subscription" element={<SubscriptionPlans />} />
          <Route path="subscription/plans" element={<SubscriptionPlans />} />
          <Route path="subscription/checkout" element={<SubscriptionCheckout />} />
          <Route path="subscription/privacy-policy" element={<SubscriptionPrivacyPolicy />} />
          <Route path="subscription/terms-of-service" element={<SubscriptionTermsOfService />} />
          <Route path="subscription/my-subscription" element={<MySubscription />} />
          <Route path="subscription/manage-payment-method" element={<ManagePaymentMethod />} />
          <Route path="subscription/billing-history" element={<BillingHistory />} />
          <Route path="subscription/cancel" element={<CancelSubscription />} />
          <Route path="subscription/reactivate" element={<ReactivatePro />} />
          <Route path="subscription/contact-support" element={<SubscriptionContactSupport />} />
        </Route>
      </Route>
      <Route path="/dashboard" element={<OnboardingTrainerDashboard />}>
        <Route index element={<DashboardOverview />} />
        <Route path="welcome" element={<SubscriptionWelcome />} />
        <Route path="clients" element={<DashboardClients />} />
        <Route path="clients/:uid" element={<DashboardClientOverview />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<OverviewTab />} />
          <Route path="overview/notes" element={<ClientNotesTab />} />
          <Route path="program" element={<ProgramTab />} />
          <Route path="workouts" element={<WorkoutsTab />} />
          <Route path="client-profile" element={<ClientProfileTab />} />
          <Route path="workouts/:workoutId" element={<DashboardWorkoutDetails />} />
        </Route>
        <Route path="programs" element={<DashboardPrograms />} />
        <Route path="messages" element={<DashboardMessagesLayout />}>
          <Route index element={<DashboardMessagesIndexRoute />} />
          <Route path=":chatId" element={<DashboardChatDetails />} />
          <Route path=":chatId/media" element={<DashboardMediaLinksFilesScreen />} />
          <Route path="checkin/:checkinId" element={<CheckinDetailsRoute />} />
        </Route>
        <Route path="profile" element={<DashboardProfile />} />
        <Route path="profile/schedule" element={<DashboardSchedule mode="profile" />} />
        <Route path="program-generator/:by" element={<ProgramGeneratorAI />} />
        <Route path="program-generated" element={<ProgramGenerated />} />
        <Route path="program-generated/:programId" element={<ProgramGenerated />} />
        <Route path="program-generated/:programId/:workout" element={<ProgramGenerated />} />

        {/* Subscription Flow Routes */}
        <Route path="subscription" element={<SubscriptionPlans />} />
        <Route path="subscription/plans" element={<SubscriptionPlans />} />
        <Route path="subscription/checkout" element={<SubscriptionCheckout />} />
        <Route path="subscription/privacy-policy" element={<SubscriptionPrivacyPolicy />} />
        <Route path="subscription/terms-of-service" element={<SubscriptionTermsOfService />} />
        <Route path="subscription/my-subscription" element={<MySubscription />} />
        <Route path="subscription/manage-payment-method" element={<ManagePaymentMethod />} />
        <Route path="subscription/billing-history" element={<BillingHistory />} />
        <Route path="subscription/cancel" element={<CancelSubscription />} />
        <Route path="subscription/reactivate" element={<ReactivatePro />} />
        <Route path="subscription/contact-support" element={<SubscriptionContactSupport />} />
      </Route>
      <Route path="about" element={<About />} />
      <Route path="loading" element={<Loading />} />
      <Route path="error" element={<ServerError />} />
      <Route path="unauthorized" element={<Unauthorized />} />
      <Route path="maintenance" element={<Maintenance />} />
      <Route path="coming-soon" element={<ComingSoon />} />
      <Route path="account-status" element={<AccountStatus />} />
      <Route path="privacy-policy" element={<PrivacyPolicy />} />
      <Route path="legal/privacyPolicy" element={<PrivacyPolicy />} />
      <Route path="terms-of-service" element={<TermsOfService />} />
      {/* 404 (must be last) */}
      <Route path="*" element={<NotFound />} />
    </Route>,
  ),
  {
    basename: process.env.APP_BASENAME ?? '',
    future: { v7_relativeSplatPath: true },
  },
);

// Service worker & app deep-linking message listener
if (typeof window !== 'undefined') {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'navigate' && event.data.url) {
        router.navigate(event.data.url);
      }
    });
  }

  window.addEventListener('app:navigate', (event: Event) => {
    const customEvent = event as CustomEvent<string>;
    if (customEvent.detail) {
      router.navigate(customEvent.detail);
    }
  });
}

root.render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AppManager>
          <RouterProvider router={router} />
        </AppManager>
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>,
);
